import { post, withoutActiveRole } from '@/lib/apiClient';

/**
 * The web portal's menus from Fabrica (POST /access/Menu/GetMenuListByRoles, DomainId 3):
 * DebtCollectify's menu call. Each of the signed-in person's roles gets its menu tree with
 * their own overrides; a menu a role lost is simply not in its tree.
 */

/** One menu as the procedure returns it (DebtCollectify's names). */
interface MenuNodeJson {
  MenuId: number;
  ParentMenuId: number;
  MenuDisplayName: string;
  RoutingPath?: string | null;
  Icon?: string | null;
  MenuOrder?: number | null;
  IsVisible?: boolean | number | null;
  Children?: MenuNodeJson[];
}

export interface FabricaMenu {
  menuId: number;
  label: string;
  path: string | null;
  icon: string | null;
  children: FabricaMenu[];
}

/** Menu trees by role id. */
export type FabricaMenus = Record<number, FabricaMenu[]>;

/** The web portal's menus (DomainId 3); the mobile app asks for 4. */
export const WEB_MENU_DOMAIN = 3;

const toMenu = (node: MenuNodeJson): FabricaMenu => ({
  menuId: node.MenuId,
  label: node.MenuDisplayName,
  path: node.RoutingPath || null,
  icon: node.Icon || null,
  children: (node.Children ?? []).filter(visible).map(toMenu),
});

const visible = (node: MenuNodeJson) => node.IsVisible !== false && node.IsVisible !== 0;

/** Reads the procedure's JSON ({ userRoleList: "[{ roleId, menus }]" }) into trees by role. */
export function parseMenuList(userRoleList: string): FabricaMenus {
  const roles = JSON.parse(userRoleList || '[]') as { roleId: number; menus: MenuNodeJson[] }[];
  return Object.fromEntries(
    roles.map((r) => [r.roleId, (r.menus ?? []).filter(visible).map(toMenu)]),
  );
}

export async function fetchWebMenus(
  token: string,
  roleIds: number[],
  scope?: { organizationId: number; branchId?: number | null },
): Promise<FabricaMenus> {
  if (roleIds.length === 0) return {};
  const answer = await post<{ userRoleList: string }>(
    '/access/Menu/GetMenuListByRoles',
    { userRoleIdList: roleIds.map((roleId) => ({ roleId })), domainId: WEB_MENU_DOMAIN },
    withoutActiveRole({
      headers: {
        Authorization: `Bearer ${token}`,
        ...(scope
          ? {
              'X-Organization-Id': String(scope.organizationId),
              ...(scope.branchId ? { 'X-Branch-Id': String(scope.branchId) } : {}),
            }
          : {}),
      },
    }),
  );
  return parseMenuList(answer.userRoleList);
}

/** Every path the menus lead to. */
export function menuPaths(menus: FabricaMenu[]): string[] {
  return menus.flatMap((m) => [...(m.path ? [m.path] : []), ...menuPaths(m.children)]);
}
